import {
  CheckoutSession,
  Currency,
  DownPaymentCollection,
  getMerchantStructureByMerchantId,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { ItemMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ItemMap';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { ENV_API_URL, ENV_HPP_URL } from '@/config/envVars';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';

export const useRestoreDataFromCheckoutSession = (checkoutSession: CheckoutSession) => {
  const {
    state: { cart },
    actions: { updateApplicationContext, resetApplicationContext },
  } = useApplicationContext();
  const { createNewCartFromItems } = useCart();
  const { createPaymentMethodEntities, updateAgentPaymentConfiguration } = usePaymentMethods();

  const [loading, setLoading] = useState<boolean>(true);

  const restore = async (doRestoreAllData?: boolean) => {
    setLoading(true);

    resetApplicationContext();
    updateApplicationContext({ checkoutSessionId: checkoutSession.id });
    await restoreStructure(checkoutSession);
    restoreCart(checkoutSession);

    if (doRestoreAllData) {
      await restoreOtherData(checkoutSession);
    }

    setLoading(false);
  };

  const restoreStructure = async (checkoutSession: CheckoutSession) => {
    const merchantStructureEntity = await findCheckoutSessionMerchantStructureEntity(checkoutSession);

    if (!merchantStructureEntity) {
      console.error('Not able to restore checkout session merchant structure');
      return;
    }

    const selectedMerchant = merchantStructureEntity.getMerchant();
    const selectedChannel = merchantStructureEntity.getChannel();
    const selectedAgent = merchantStructureEntity.getAgent();

    if (!selectedMerchant || !selectedChannel || !selectedAgent) {
      console.error('Not able to restore checkout session merchant structure');
      return;
    }

    updateApplicationContext({
      selectedMerchant,
      selectedChannel,
      selectedAgent,
      selectedAgentHasError: false,
    });
  };

  const restoreCart = (checkoutSession: CheckoutSession) => {
    const cartItems = ItemMap.fromCheckoutSessionToItems(checkoutSession);
    createNewCartFromItems(cartItems, checkoutSession.purchaseCountry);
  };

  const createPaymentMethodsEntitiesFromCheckoutSession = async (checkoutSession: CheckoutSession) => {
    updateApplicationContext({
      downPayment: {
        amount: {
          currency: checkoutSession.downPayment?.amount.currency ?? Currency.EUR,
          amount: checkoutSession.downPayment?.amount.amount ?? 0,
        },
        downPaymentCollection: checkoutSession.downPayment?.downPaymentCollection ?? DownPaymentCollection.NONE,
      },
    });

    await updateAgentPaymentConfiguration(checkoutSession.agentId);
    createPaymentMethodEntities();
  };

  const restoreOtherData = async (checkoutSession: CheckoutSession) => {
    updateApplicationContext({
      orderReference1: checkoutSession.cart.merchantOrderReference1 ?? undefined,
      orderReference2: checkoutSession.cart.merchantOrderReference2 ?? undefined,
      qrCodeUrl: `${ENV_API_URL}v2/checkout/${checkoutSession.id}/hpp/qr/`,
      customerData: checkoutSession.customer?.fullName
        ? {
            firstName: checkoutSession.customer.fullName.firstName,
            lastName: checkoutSession.customer.fullName.lastName,
            email: checkoutSession.customer.email ?? undefined,
            phoneNumber: checkoutSession.customer.phoneNumber ?? undefined,
            documentNumber: checkoutSession.customer.documentNumber ?? undefined,
          }
        : undefined,
      ...(checkoutSession.hpp?.id && {
        hppId: checkoutSession.hpp?.id,
        redirectUrl: `${ENV_HPP_URL}${checkoutSession.hpp?.id}`,
      }),
    });
  };

  const reset = () => {
    resetApplicationContext();
  };

  return {
    restore,
    loading,
    createPaymentMethodsEntitiesFromCheckoutSession,
    reset,
    cart,
  };
};

const findCheckoutSessionMerchantStructureEntity = async (
  checkoutSession: CheckoutSession,
): Promise<MerchantStructureEntity | undefined> => {
  try {
    const merchantStructure = await getMerchantStructureByMerchantId({ merchantId: checkoutSession.merchantId });

    return MerchantStructureEntity.create({
      merchantStructure,
      agentId: checkoutSession.agentId,
    });
  } catch (err) {
    console.error('Not able to restore checkout session merchant structure', err);
  }
};
